import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useNotification } from '../../../../contexts/NotificationContext';
import { usePuppyMode } from '../../../../hooks';
import { DEFAULT_PAGE_SIZE } from '../../constants';
import { useSearchUsers } from '../../hooks';
import { UsersHeader } from '../UsersHeader';
import { UsersTable } from '../UsersTable';

interface UserListProps {}

export const UserList: React.FC<UserListProps> = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState(
    searchParams.get('search') || ''
  );
  const { addNotification } = useNotification();
  const { puppyFilter } = usePuppyMode();

  const captureException = useCallback(() => {
    addNotification({
      title: 'Error Loading Users',
      message: 'An error occurred while searching for users. Please try again.',
      type: 'error',
    });
  }, [addNotification]);

  const searchConfig = useMemo(
    () => ({
      initialPageSize: DEFAULT_PAGE_SIZE,
      captureException,
    }),
    [captureException]
  );

  const { users, totalUsers, isLoading, searchUsers, refetch } =
    useSearchUsers(searchConfig);

  // Update URL when search term changes
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (searchTerm) {
      params.set('search', searchTerm);
    } else {
      params.delete('search');
    }
    setSearchParams(params);
  }, [searchTerm, setSearchParams, searchParams]);

  // Initial load and search effect
  useEffect(() => {
    const queryParams = {
      ...(searchTerm && { keywords: searchTerm }),
      filter: {
        ...(puppyFilter !== undefined && { isPuppy: puppyFilter }),
      },
      limit: pageSize,
    };

    searchUsers(queryParams);
  }, [searchTerm, puppyFilter, pageSize, searchUsers]);

  useEffect(() => {
    document.title = 'User Management - Choco Admin';
  }, []);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  return (
    <div className="p-6 min-h-screen bg-background">
      <UsersHeader />
      <UsersTable
        users={users}
        totalUsers={totalUsers}
        currentPage={currentPage}
        pageSize={pageSize}
        loading={isLoading}
        searchTerm={searchTerm}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onSearch={handleSearch}
        onRefresh={() => refetch()}
      />
    </div>
  );
};
